import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore
} from '@mui/icons-material';

const DigitalWorkplace = () => {
  const services = [
    {
      title: 'Remote Work Solutions',
      description: 'Comprehensive solutions for secure and productive remote work',
      features: ['VPN and Remote Access', 'Virtual Desktop Infrastructure', 'Collaboration Tools', 'Security Management'],
      benefits: ['Secure Remote Access', 'Improved Productivity', 'Cost Reduction', 'Enhanced Collaboration']
    },
    {
      title: 'Unified Communications',
      description: 'Integrate communication tools for seamless collaboration',
      features: ['Video Conferencing', 'Instant Messaging', 'Voice Solutions', 'Presence Management'],
      benefits: ['Better Communication', 'Reduced Travel Costs', 'Improved Efficiency', 'Global Connectivity']
    },
    {
      title: 'Digital Workspace Management',
      description: 'Manage and optimize digital workspaces for maximum efficiency',
      features: ['Workspace Analytics', 'User Experience Optimization', 'Device Management', 'Application Delivery'],
      benefits: ['Optimized Performance', 'Better User Experience', 'Reduced Support', 'Increased Productivity']
    },
    {
      title: 'Employee Experience Platforms',
      description: 'Create engaging digital experiences for employees',
      features: ['Employee Portals', 'Self-Service Solutions', 'Mobile Applications', 'Workflow Automation'],
      benefits: ['Improved Engagement', 'Faster Service Delivery', 'Reduced IT Tickets', 'Better Satisfaction']
    }
  ];

  const customerStories = [
    {
      title: 'Global Technology Company',
      description: 'Transforming remote work capabilities for 50,000+ employees',
      outcome: 'Implemented comprehensive digital workplace solutions enabling seamless remote work for all employees, resulting in 30% improvement in productivity and 50% reduction in IT support tickets.',
      industry: 'Technology',
      services: ['Remote Work Solutions', 'Unified Communications']
    },
    {
      title: 'Financial Services Organization',
      description: 'Enhancing security and collaboration for distributed teams',
      outcome: 'Deployed secure digital workplace platform with advanced collaboration tools, enabling secure remote work while maintaining compliance with financial regulations.',
      industry: 'Financial Services',
      services: ['Security Management', 'Collaboration Tools']
    }
  ];

  const faqs = [
    {
      question: 'What is a digital workplace?',
      answer: 'A digital workplace is a technology-enabled environment that supports modern work practices by providing employees with the tools, applications, and services they need to work effectively from anywhere. It includes collaboration tools, communication platforms, and secure access to business applications.'
    },
    {
      question: 'How can digital workplace solutions improve productivity?',
      answer: 'Digital workplace solutions improve productivity by providing seamless access to tools and information, enabling better collaboration, automating routine tasks, and supporting flexible work arrangements. They reduce friction in daily work processes and enable employees to focus on high-value activities.'
    },
    {
      question: 'What security considerations are important for digital workplaces?',
      answer: 'Digital workplace security requires comprehensive identity and access management, endpoint security, data protection, network security, and compliance monitoring. We implement zero-trust security models and ensure all solutions meet industry security standards and regulatory requirements.'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 12,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 3
            }}
          >
            Digital Workplace Services
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              maxWidth: '800px',
              mx: 'auto',
              mb: 4,
              lineHeight: 1.6
            }}
          >
            Transform your workplace with modern digital solutions that enable seamless collaboration and productivity
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#1565c0' }
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: '#1565c0',
                  color: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              Learn More
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* What We Do Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Enable Modern Work Practices
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              maxWidth: '800px',
              mx: 'auto',
              mb: 8,
              lineHeight: 1.6
            }}
          >
            Our digital workplace solutions help organizations adapt to modern work requirements by providing 
            secure, flexible, and productive environments that support remote work, collaboration, and employee engagement.
          </Typography>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Our Digital Workplace Services
          </Typography>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {service.description}
                    </Typography>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        Key Features:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {service.features.map((feature, idx) => (
                          <Chip
                            key={idx}
                            label={feature}
                            size="small"
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>

                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        Benefits:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {service.benefits.map((benefit, idx) => (
                          <Chip
                            key={idx}
                            label={benefit}
                            size="small"
                            sx={{
                              bgcolor: '#f3e5f5',
                              color: '#7b1fa2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Customer Stories Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Customer Success Stories
          </Typography>

          <Grid container spacing={4}>
            {customerStories.map((story, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {story.title}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1976d2',
                        mb: 2
                      }}
                    >
                      {story.description}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {story.outcome}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip
                        label={story.industry}
                        sx={{
                          bgcolor: '#e8f5e8',
                          color: '#2e7d32',
                          fontWeight: 'bold'
                        }}
                      />
                      <Stack direction="row" spacing={1}>
                        {story.services.map((service, idx) => (
                          <Chip
                            key={idx}
                            label={service}
                            size="small"
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                elevation={0}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  mb: 2,
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor: 'white',
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: 'white', borderRadius: '0 0 8px 8px' }}>
                  <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, bgcolor: '#1976d2', color: 'white' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              backdropFilter: 'blur(10px)'
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3
              }}
            >
              Transform Your Workplace
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9
              }}
            >
              Get expert guidance on implementing modern digital workplace solutions
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Request a Consultation
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default DigitalWorkplace;
